/** @jsx jsx */

import React from "react";
import { jsx } from "@emotion/core";
import { Formik, Form, FormikHelpers } from "formik";
import { Prompt } from "react-router-dom";

//components , hooks
import Button from "../../../components/button";
import {
  FormInput,
  FormDatePicker,
  FormRadioButton,
  FormInputNumber
} from "../../../components/formHelpers";
import useLoanStatus from "../../../hooks/auth/useLoanStatus";
import useRedirect from "../../../helperHooks/useRedirect";

// Styles, Objects, constants.
import { commonFoldStyles } from "../EligibilityViewStyles";
import { EligibilityRoutes } from "../../../Routes";
import { SaveEligibilityRequest } from "../../../store/eligibility/actions";
import { EligibilityDetailsState } from "../../../store/eligibility/state";
import { ApplicantDetails } from "../../../services/eligibilityService/EligibilityService.type";
import {
  applicantDetailsFoldConstants as CONSTANTS,
  applicantDetailsFoldValidationSchema as validationSchema,
  booleanOptions
} from "./ApplicantDetailsFoldConstants";
import { PropertyType } from "../EligibilityViewConstants";
import { unsavedChangeAlert } from "../../../Constants";

export type ApplicantDetailsState = ApplicantDetails;

interface Props {
  eligibilityDetails: EligibilityDetailsState;
  submitting: boolean;
  saveEligibilityDetails: (data: SaveEligibilityRequest) => void;
}

const ApplicantDetailsFold: React.FC<Props> = (props: Props) => {
  const {
    eligibilityDetails: { applicantDetails },
    saveEligibilityDetails,
    submitting
  } = props;

  const redirect = useRedirect();

  const { disableEligibilityForms } = useLoanStatus();

  const handleSubmit = (
    values: ApplicantDetailsState,
    formikHelpers: FormikHelpers<ApplicantDetailsState>
  ) => {
    const {
      currentAddress,
      currentAddressType,
      currentAddressPincode,
      currentAddressState,
      permanentAddress,
      permanentAddressType,
      permanentAddressPincode,
      permanentAddressState,
      isPermanentAddressSameAsCurrentAddress,
      ...others
    } = values;
    const payload: ApplicantDetails = {
      ...others,
      currentAddress,
      currentAddressType,
      currentAddressPincode,
      currentAddressState,
      isPermanentAddressSameAsCurrentAddress,
      permanentAddress: isPermanentAddressSameAsCurrentAddress
        ? currentAddress
        : permanentAddress,
      permanentAddressType: isPermanentAddressSameAsCurrentAddress
        ? currentAddressType
        : permanentAddressType,
      permanentAddressPincode: isPermanentAddressSameAsCurrentAddress
        ? currentAddressPincode
        : permanentAddressPincode,
      permanentAddressState: isPermanentAddressSameAsCurrentAddress
        ? currentAddressState
        : permanentAddressState
    };
    saveEligibilityDetails({
      data: {
        applicantDetails: payload
      },
      onSuccess: () => {
        //to reset initial state, so that, form is not "dirty"
        formikHelpers.resetForm();
        redirect(routes =>
          routes.eligibility.view(EligibilityRoutes.ReceivablesFold)
        );
      }
    });
  };

  return (
    <div css={commonFoldStyles}>
      <Formik
        initialValues={applicantDetails}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnChange
        validateOnBlur
      >
        {({ values, dirty }) => (
          <Form>
            <Prompt when={dirty} message={unsavedChangeAlert} />
            <div className="grid-container">
              <FormInput
                name="firstName"
                className="mb-3 mb-md-0"
                label={CONSTANTS.labels.firstName}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInput
                name="middleName"
                className="mb-3 mb-md-0"
                label={CONSTANTS.labels.middleName}
                disabled={disableEligibilityForms}
              />
              <FormInput
                name="lastName"
                className="mb-3 mb-md-0"
                label={CONSTANTS.labels.lastName}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormDatePicker
                name="dateOfBirth"
                className="mb-3 mb-md-0"
                format={CONSTANTS.labels.dateFormat}
                label={CONSTANTS.labels.dateOfBirth}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInput
                className="mb-3 mb-md-0"
                name="currentAddress"
                label={CONSTANTS.labels.currentAddress}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInput
                className="mb-3 mb-md-0"
                name="currentAddressState"
                label={CONSTANTS.labels.currentAddressState}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInput
                className="mb-3 mb-md-0"
                name="city"
                label={CONSTANTS.labels.city}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInput
                className="mb-3 mb-md-0"
                name="currentAddressPincode"
                label={CONSTANTS.labels.currnentAddressPincode}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInputNumber
                className="mb-3 mb-md-0"
                name="residenceStability"
                label={CONSTANTS.labels.residenceStability}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormRadioButton
                name="currentAddressType"
                className="mb-3 mb-md-0"
                options={PropertyType}
                label={CONSTANTS.labels.currentAddressType}
                disabled={disableEligibilityForms}
                mandatory
              />
              <FormInput
                name="panNumber"
                className="mb-3 mb-md-0"
                label={CONSTANTS.labels.panNumber}
                disabled={disableEligibilityForms}
                mandatory
              />
              <div className="full-width mb-3 mb-md-0">
                <FormRadioButton
                  name="isPermanentAddressSameAsCurrentAddress"
                  options={booleanOptions}
                  label={CONSTANTS.labels.addressSame}
                  disabled={disableEligibilityForms}
                />
                {!values.isPermanentAddressSameAsCurrentAddress && (
                  <label className="d-block pt-4">
                    {CONSTANTS.labels.addressNotSameNote}
                  </label>
                )}
              </div>

              {!values.isPermanentAddressSameAsCurrentAddress && (
                <React.Fragment>
                  <FormInput
                    className="mb-3 mb-md-0"
                    name="permanentAddress"
                    label={CONSTANTS.labels.PermanentAddress}
                    disabled={disableEligibilityForms}
                    mandatory
                  />
                  <FormRadioButton
                    name="permanentAddressType"
                    className="mb-3 mb-md-0"
                    options={PropertyType}
                    label={CONSTANTS.labels.PermanentAddressType}
                    disabled={disableEligibilityForms}
                    mandatory
                  />
                  <FormInput
                    className="mb-3 mb-md-0"
                    name="permanentAddressState"
                    label={CONSTANTS.labels.permanentAddressState}
                    disabled={disableEligibilityForms}
                    mandatory
                  />
                  <FormInput
                    className="mb-3 mb-md-0"
                    name="permanentAddressCity"
                    label={CONSTANTS.labels.permanentAddressCity}
                    disabled={disableEligibilityForms}
                    mandatory
                  />
                  <FormInput
                    className="mb-3 mb-md-0"
                    name="permanentAddressPincode"
                    label={CONSTANTS.labels.PermanentAddressPincode}
                    disabled={disableEligibilityForms}
                    mandatory
                  />
                </React.Fragment>
              )}
            </div>
            {!disableEligibilityForms && (
              <Button
                htmlType="submit"
                type="primary"
                disabled={submitting}
                loading={submitting}
                containerDivClassName="justify-content-md-end"
                className="mt-5 mb-5"
              >
                {CONSTANTS.buttons.next}
              </Button>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ApplicantDetailsFold;
